// ─────────────────────────────────────────────────────────────────────────────
// queueAutoReplay.js — Rejeu SILENCIEUX au démarrage (Chantier B, 01/08/2026).
//
// Contrairement à QueueRecovery.jsx (rep7, manuel, écran de validation avec
// bouton "Renvoyer"), als7 (pointage) et controles7 (contrôles qualité) sont
// rejoués AUTOMATIQUEMENT au démarrage, sans aucune action de l'utilisateur.
// Décision Claire, 01/08/2026, après 2 incidents de perte de données le même
// jour (pointage + contrôle qualité, tous deux perdus par un retry qui vit
// uniquement en mémoire JS et meurt si l'app passe en arrière-plan).
//
// Choix assumé : pas d'écran, pas de clic. Le risque de conflit est faible
// (id unique par pointage/contrôle, lecture-avant-écriture pour als7, merge
// par id pour controles7) — l'enjeu prioritaire est qu'aucune donnée terrain
// ne dépende de la compréhension d'un écran de reprise par un salarié pressé.
//
// RÈGLE ABSOLUE (même principe que localQueue.js §8.1) : échoue TOUJOURS en
// silence (log console uniquement), ne bloque jamais le démarrage de l'app.
// Une entrée qui échoue encore reste 'pending' et sera retentée au prochain
// démarrage — elle ne disparaît que lorsque confirmByRecordIds la marque
// confirmée après un succès réseau réel (voir supabase.js/useSt/trySave).
// ─────────────────────────────────────────────────────────────────────────────

import { listAll, confirmByRecordIds, reconstructControlePhotos } from './localQueue.js';
import { supaGet, supaSet } from './supabase.js';
import { appliquerMarquageControle } from './utils.js';

// ── als7 (pointage) ───────────────────────────────────────────────────────
// Rejoue chaque entrée en attente avec la MÊME logique de lecture-avant-écriture
// que punch() (sécurité multi-session : ne jamais écraser un pointage posé par
// quelqu'un d'autre entre-temps). Séquentiel, jamais en parallèle (même principe
// que QueueRecovery §8.3).
export async function autoReplayAls7({ setAlsSynced }) {
  let entries;
  try {
    entries = (await listAll({ includeConfirmed: false })).filter(e => e.key === 'als7');
  } catch (e) {
    console.error('[queueAutoReplay] als7 listAll échoué', e);
    return;
  }
  for (const entry of entries) {
    try {
      const base = await supaGet('als7') || [];
      const already = base.some(x => x.id === entry.payload.id);
      const merged = already ? base : [...base, entry.payload];
      if (!already) await supaSet('als7', merged);
      await confirmByRecordIds('als7', [entry.recordId]);
      if (setAlsSynced) setAlsSynced(merged);
      console.log('[queueAutoReplay] als7 rejeu réussi', entry.recordId);
    } catch (e) {
      console.error('[queueAutoReplay] als7 rejeu échoué (retentera au prochain démarrage)', entry.recordId, e && e.message);
    }
  }
}

// ── controles7 ────────────────────────────────────────────────────────────
// Reconstruit les photos depuis les Blobs (§ voir localQueue.js), puis pousse
// dans setControles. La confirmation viendra de la branche controles7 déjà
// ajoutée dans useSt/trySave (comme rep7) une fois l'écriture réseau réussie —
// cette fonction amorce juste le pipeline normal, elle ne confirme rien elle-même.
export async function autoReplayControles7({ setControles, setRep }) {
  let entries;
  try {
    entries = (await listAll({ includeConfirmed: false })).filter(e => e.key === 'controles7');
  } catch (e) {
    console.error('[queueAutoReplay] controles7 listAll échoué', e);
    return;
  }
  for (const entry of entries) {
    try {
      const full = await reconstructControlePhotos(entry);
      if (!full) continue;
      setControles(prev => {
        const i = prev.findIndex(x => x.id === full.id);
        return i >= 0 ? prev.map(x => x.id === full.id ? full : x) : [...prev, full];
      });
      // Le marquage ctrlId/ctrlVerdict/ctrlAt sur le rapport visé n'est protégé par
      // AUCUNE file : saveCtrl l'écrit dans rep7 en direct, et si le réseau tombe il
      // est perdu — le rapport reste affiché « non contrôlé » indéfiniment. On le
      // rejoue donc ici. ecraser:false — ce contrôle peut dater de plusieurs jours,
      // il ne doit jamais écraser un contrôle plus récent posé entre-temps.
      if (setRep) setRep(prev => appliquerMarquageControle(prev, full, { ecraser: false }));
      console.log('[queueAutoReplay] controles7 réinjecté (+ marquage rapport), confirmation en attente du succès réseau', entry.recordId);
    } catch (e) {
      console.error('[queueAutoReplay] controles7 rejeu échoué (retentera au prochain démarrage)', entry.recordId, e && e.message);
    }
  }
}
